/**
 * Administrator console data access.
 *
 * Query keys are local to this module instead of the shared factory: the console
 * is one self-contained area, and keeping its keys here means a list page and
 * the mutation that invalidates it can never disagree about a string.
 *
 * Every hook returns server truth. Nothing about permissions is derived here —
 * the capability list comes from the session and the server re-checks every
 * call, so a stale client can only render a button that then fails.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  ActivationCodeDto,
  AdminCalibrationDto,
  AdminLookupMissesDto,
  AdminProviderStatusDto,
  AdminApplicationDetailDto,
  AdminApplicationDto,
  AdminDataStatusDto,
  AdminDto,
  AdminReferralDto,
  AdminReferralSignupDto,
  AdminSettingsDto,
  AdminSummaryDto,
  AdminUserDetailDto,
  AdminUserDto,
  PagedResponse,
} from '@shared/types'
import { type AdminCapability, type AdminRole } from '@shared/constants'

import { apiRequest } from '../lib/api'

/** Query keys for the console. */
export const adminKeys = {
  root: ['admin'] as const,
  summary: ['admin', 'summary'] as const,
  applications: (status: string, page: number) => ['admin', 'applications', status, page] as const,
  application: (id: string) => ['admin', 'applications', id] as const,
  codes: (state: string, email: string, page: number) =>
    ['admin', 'codes', state, email, page] as const,
  users: (query: string, page: number) => ['admin', 'users', query, page] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  admins: (page: number) => ['admin', 'admins', page] as const,
  referrals: (page: number) => ['admin', 'referrals', page] as const,
  referral: (code: string, page: number) => ['admin', 'referrals', code, page] as const,
  settings: ['admin', 'settings'] as const,
  dataStatus: ['admin', 'data-status'] as const,
  providers: ['admin', 'providers'] as const,
  lookupMisses: (page: number) => ['admin', 'lookup-misses', page] as const,
  calibration: ['admin', 'calibration'] as const,
}

/** Invalidate anything a console mutation could have changed. */
function invalidateConsole(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: adminKeys.root })
}

export function useAdminSummary() {
  return useQuery({
    queryKey: adminKeys.summary,
    queryFn: () => apiRequest<AdminSummaryDto>('/api/admin/summary'),
  })
}

export function useAdminApplications(status: string, page: number) {
  return useQuery({
    queryKey: adminKeys.applications(status, page),
    queryFn: () =>
      apiRequest<PagedResponse<AdminApplicationDto>>(
        `/api/admin/applications?status=${encodeURIComponent(status)}&page=${page}`,
      ),
  })
}

export function useAdminApplication(id: string) {
  return useQuery({
    queryKey: adminKeys.application(id),
    queryFn: () => apiRequest<AdminApplicationDetailDto>(`/api/admin/applications/${id}`),
    enabled: id.length > 0,
  })
}

/** Approves a request and mails the invite code. */
export function useApproveApplication() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ applicationId: string; code: ActivationCodeDto; delivered: boolean }>(
        `/api/admin/applications/${id}/approve`,
        { method: 'POST' },
      ),
    onSettled: () => invalidateConsole(client),
  })
}

/** Rejects a request. The reason is the text the applicant will read. */
export function useRejectApplication() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      apiRequest<{ application: AdminApplicationDto }>(
        `/api/admin/applications/${input.id}/reject`,
        { method: 'POST', body: { reason: input.reason } },
      ),
    onSettled: () => invalidateConsole(client),
  })
}

export function useAdminCodes(state: string, email: string, page: number) {
  return useQuery({
    queryKey: adminKeys.codes(state, email, page),
    queryFn: () =>
      apiRequest<PagedResponse<ActivationCodeDto>>(
        `/api/admin/codes?state=${encodeURIComponent(state)}&email=${encodeURIComponent(email)}&page=${page}`,
      ),
  })
}

export function useIssueCode() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; note?: string | null; ttlHours?: number }) =>
      apiRequest<{ code: ActivationCodeDto; delivered: boolean }>('/api/admin/codes', {
        method: 'POST',
        body: input,
      }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useResendCode() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ codeId: string; delivered: boolean }>(`/api/admin/codes/${id}/resend`, {
        method: 'POST',
      }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useRevokeCode() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ code: ActivationCodeDto }>(`/api/admin/codes/${id}/revoke`, { method: 'POST' }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useAdminUsers(query: string, page: number) {
  return useQuery({
    queryKey: adminKeys.users(query, page),
    queryFn: () =>
      apiRequest<PagedResponse<AdminUserDto>>(
        `/api/admin/users?query=${encodeURIComponent(query)}&page=${page}`,
      ),
  })
}

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: adminKeys.user(id),
    queryFn: () => apiRequest<AdminUserDetailDto>(`/api/admin/users/${id}`),
    enabled: id.length > 0,
  })
}

export function useActivateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ user: AdminUserDto }>(`/api/admin/users/${id}/activate`, { method: 'POST' }),
    onSettled: () => invalidateConsole(client),
  })
}

/** Suspends an account; `permanent` is stored as a distant expiry. */
export function useBanUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; days?: number; permanent?: boolean; reason?: string }) =>
      apiRequest<{ user: AdminUserDto }>(`/api/admin/users/${input.id}/ban`, {
        method: 'POST',
        body: {
          ...(input.permanent ? { permanent: true } : { days: input.days ?? 1 }),
          ...(input.reason ? { reason: input.reason } : {}),
        },
      }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useUnbanUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ user: AdminUserDto }>(`/api/admin/users/${id}/unban`, { method: 'POST' }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useAdmins(page: number) {
  return useQuery({
    queryKey: adminKeys.admins(page),
    queryFn: () => apiRequest<PagedResponse<AdminDto>>(`/api/admin/admins?page=${page}`),
  })
}

export function useCreateAdmin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: AdminRole; note?: string | null }) =>
      apiRequest<{ admin: AdminDto }>('/api/admin/admins', { method: 'POST', body: input }),
    onSettled: () => invalidateConsole(client),
  })
}

export function useUpdateAdmin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      id: string
      role?: AdminRole
      status?: 'active' | 'disabled'
      capabilitiesExtra?: AdminCapability[]
      capabilitiesDenied?: AdminCapability[]
      note?: string | null
    }) => {
      const { id, ...patch } = input
      return apiRequest<{ admin: AdminDto }>(`/api/admin/admins/${id}`, {
        method: 'PATCH',
        body: patch,
      })
    },
    onSettled: () => invalidateConsole(client),
  })
}

export function useReferrals(page: number) {
  return useQuery({
    queryKey: adminKeys.referrals(page),
    queryFn: () => apiRequest<PagedResponse<AdminReferralDto>>(`/api/admin/referrals?page=${page}`),
  })
}

export function useReferralSignups(code: string, page: number) {
  return useQuery({
    queryKey: adminKeys.referral(code, page),
    queryFn: () =>
      apiRequest<PagedResponse<AdminReferralSignupDto>>(
        `/api/admin/referrals/${encodeURIComponent(code)}?page=${page}`,
      ),
    enabled: code.length > 0,
  })
}

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings,
    queryFn: () => apiRequest<AdminSettingsDto>('/api/admin/settings'),
  })
}

/**
 * The data and configuration board: which districts exist and what reference data
 * is stored behind them. Read-only, so there is no matching mutation. The district
 * list is paged on the server like every other console list.
 */
export function useAdminDataStatus(page: number) {
  return useQuery({
    queryKey: [...adminKeys.dataStatus, page],
    queryFn: () => apiRequest<AdminDataStatusDto>(`/api/admin/data-status?page=${page}`),
  })
}

export function useUpdateSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<AdminSettingsDto>) =>
      apiRequest<AdminSettingsDto>('/api/admin/settings', { method: 'PATCH', body: patch }),
    onSettled: () => invalidateConsole(client),
  })
}

/**
 * Map budget, breakers and the read-only switch.
 *
 * The numbers come from the same tables the gate writes, so the console cannot
 * disagree with the engine about what has been spent. It is polled while the page
 * is open because a round in another tab is exactly what an operator watching the
 * budget is looking at.
 */
export function useAdminProviders() {
  return useQuery({
    queryKey: adminKeys.providers,
    queryFn: () => apiRequest<AdminProviderStatusDto>('/api/admin/providers'),
    refetchInterval: 30_000,
  })
}

/** Clears one standing block, which is what an operator does after a fix. */
export function useResetBreaker() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { provider: string; service: string }) =>
      apiRequest<{ cleared: boolean }>(
        `/api/admin/providers/${encodeURIComponent(input.provider)}/${encodeURIComponent(input.service)}/breaker/reset`,
        { method: 'POST' },
      ),
    onSettled: () => invalidateConsole(client),
  })
}

/** Departure-point gaps, most requested first. */
export function useAdminLookupMisses(page: number) {
  return useQuery({
    queryKey: adminKeys.lookupMisses(page),
    queryFn: () => apiRequest<AdminLookupMissesDto>(`/api/admin/lookup-misses?page=${page}`),
  })
}

/** Drops the gaps nobody has asked for in the given number of days. */
export function usePruneLookupMisses() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (olderThanDays: number) =>
      apiRequest<{ deleted: number }>('/api/admin/lookup-misses/prune', {
        method: 'POST',
        body: { olderThanDays },
      }),
    onSettled: () => invalidateConsole(client),
  })
}

/** Calibration proposals and their status. */
export function useAdminCalibration() {
  return useQuery({
    queryKey: adminKeys.calibration,
    queryFn: () => apiRequest<AdminCalibrationDto>('/api/admin/calibration'),
  })
}

/** Fits new coefficients from the recorded samples, which only proposes. */
export function usePreviewCalibration() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiRequest<{ created: string[] }>('/api/admin/calibration/preview', { method: 'POST' }),
    onSettled: () => invalidateConsole(client),
  })
}

/** Applies one proposal to the live coefficients. */
export function useApplyCalibration() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) =>
      apiRequest<{ applied: boolean }>('/api/admin/calibration/apply', {
        method: 'POST',
        body: { runId },
      }),
    onSettled: () => invalidateConsole(client),
  })
}
